import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  ChefHat, BarChart3, Package, Users, Building2, Check, ArrowRight,
  Smartphone, Cloud, Shield, Clock, Star, Menu, X, Home, Zap, CreditCard,
  ShieldCheck, Printer, Wallet, FileCheck, QrCode, Boxes, ReceiptText,
  Send, Loader2, Quote, Mail, Phone
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { QRCodeSVG } from "qrcode.react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Decorative QR mock (no external lib) — deterministic 21x21 pattern with finder squares.
function MockQR() {
  const N = 21;
  const inFinder = (r, c) => {
    const f = (br, bc) => r >= br && r < br + 7 && c >= bc && c < bc + 7;
    return f(0, 0) || f(0, N - 7) || f(N - 7, 0);
  };
  const finderOn = (r, c) => {
    const rel = (br, bc) => { const rr = r - br, cc = c - bc; if (rr < 0 || cc < 0 || rr > 6 || cc > 6) return null; const edge = rr === 0 || rr === 6 || cc === 0 || cc === 6; const core = rr >= 2 && rr <= 4 && cc >= 2 && cc <= 4; return edge || core; };
    const a = rel(0, 0); if (a !== null) return a;
    const b = rel(0, N - 7); if (b !== null) return b;
    const d = rel(N - 7, 0); if (d !== null) return d;
    return false;
  };
  const cells = [];
  for (let r = 0; r < N; r++) {
    for (let c = 0; c < N; c++) {
      const on = inFinder(r, c) ? finderOn(r, c) : ((r * 7 + c * 5 + r * c) % 3 === 0);
      if (on) cells.push(<rect key={`${r}-${c}`} x={c} y={r} width={1.02} height={1.02} fill="#0f172a" />);
    }
  }
  return (
    <svg viewBox={`0 0 ${N} ${N}`} width={104} height={104} className="rounded-md bg-white p-1" shapeRendering="crispEdges" aria-label="Fiscal QR code">
      {cells}
    </svg>
  );
}

// Animated mock fiscal receipt for the hero.
function HeroReceipt() {
  return (
    <div className="relative mx-auto w-full max-w-xs hp-float">
      <div className="absolute -inset-6 bg-primary/20 blur-3xl rounded-full" aria-hidden="true" />
      <div className="relative bg-white rounded-xl shadow-2xl border border-gray-100 p-5 font-mono text-[11px] text-gray-700 overflow-hidden">
        <div className="hp-scan" aria-hidden="true" />
        <div className="text-center mb-3">
          <div className="mx-auto w-8 h-8 bg-primary rounded-lg flex items-center justify-center mb-2">
            <ChefHat className="h-5 w-5 text-primary-foreground" />
          </div>
          <p className="font-sans font-bold text-sm text-gray-900">The Diani Plate</p>
          <p className="text-[10px] text-gray-400">TAX INVOICE • KRA eTIMS</p>
        </div>
        <div className="border-t border-dashed border-gray-200 my-2" />
        {[["2x Nyama Choma", "1,600"], ["1x Tusker 500ml", "350"], ["1x Kachumbari", "200"]].map(([a, b]) => (
          <div key={a} className="flex justify-between py-0.5"><span>{a}</span><span>{b}</span></div>
        ))}
        <div className="border-t border-dashed border-gray-200 my-2" />
        <div className="flex justify-between"><span>Subtotal</span><span>2,150</span></div>
        <div className="flex justify-between"><span>VAT (16%)</span><span>296.55</span></div>
        <div className="flex justify-between font-sans font-bold text-gray-900 text-xs mt-1"><span>TOTAL</span><span>KSh 2,150</span></div>
        <div className="border-t border-dashed border-gray-200 my-2" />
        <div className="flex items-center gap-3">
          <div className="hp-qr-in shrink-0"><MockQR /></div>
          <div className="text-[9px] leading-tight text-gray-500">
            <p className="font-semibold text-gray-700">SCAN TO VERIFY</p>
            <p>SDC: KRAMW009202401</p>
            <p>CU INV: 0010200034</p>
            <p className="text-primary font-semibold mt-1">Fiscalised with KRA</p>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes hp-float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
        .hp-float { animation: hp-float 5s ease-in-out infinite; }
        @keyframes hp-scan { 0% { top: -20%; } 100% { top: 120%; } }
        .hp-scan { position: absolute; left: 0; right: 0; height: 40px; background: linear-gradient(to bottom, transparent, rgba(34,197,94,0.18), transparent); animation: hp-scan 3.2s ease-in-out infinite; }
        @keyframes hp-qr-in { 0% { opacity: 0; transform: scale(0.85); } 100% { opacity: 1; transform: scale(1); } }
        .hp-qr-in { animation: hp-qr-in 700ms ease-out both; animation-delay: 400ms; }
        @media (prefers-reduced-motion: reduce) { .hp-float, .hp-scan, .hp-qr-in { animation: none; } }
      `}</style>
    </div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [addons, setAddons] = useState([]);
  const [pricingConfig, setPricingConfig] = useState({
    annual_discount_months: 2,
    integration_price_per_property: 2500,
    currency: "KES"
  });
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [contact, setContact] = useState({ name: "", business_name: "", email: "", phone: "", message: "" });
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactDone, setContactDone] = useState(false);
  const [activeShot, setActiveShot] = useState(0);

  const showcase = [
    { src: "/showcase/pos-menu.png", title: "Fast point of sale", caption: "Tap-to-add menu with categories and modifiers — built for speed on the floor." },
    { src: "/showcase/pos-invoice.png", title: "Build the order", caption: "A running cart with live totals and tax, ready to send to the kitchen or bar." },
    { src: "/showcase/pos-settlement.png", title: "Settle any way they pay", caption: "Cash, M-Pesa, card or split — settled and fiscalised in seconds." },
    { src: "/showcase/qr-menu.png", title: "Scan-to-order QR menu", caption: "Guests browse and order from their phone via your branded QR menu." },
    { src: "/showcase/dashboard.png", title: "Live business dashboard", caption: "Sales, orders, payment mix and low-stock alerts — at a glance." },
    { src: "/showcase/etims.png", title: "KRA eTIMS management", caption: "Track every fiscal submission, credit note and import in one place." },
  ];

  useEffect(() => {
    const id = setInterval(() => setActiveShot((i) => (i + 1) % showcase.length), 4500);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const demoMenuUrl = (typeof window !== "undefined" ? window.location.origin : "") + "/menu/diani-plate";

  const submitContact = async (e) => {
    e.preventDefault();
    if (!contact.name.trim() || !contact.email.trim()) {
      toast.error("Please enter your name and email.");
      return;
    }
    try {
      setContactSubmitting(true);
      const res = await axios.post(`${API_URL}/api/public/contact`, { ...contact, source: "landing" });
      toast.success(res.data?.message || "Thanks! We'll be in touch shortly.");
      setContactDone(true);
      setContact({ name: "", business_name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send your message. Please try again.");
    } finally {
      setContactSubmitting(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const [plansRes, addonsRes, configRes] = await Promise.all([
          axios.get(`${API_URL}/api/public/plans`),
          axios.get(`${API_URL}/api/public/addons`).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/api/public/pricing-config`).catch(() => ({ data: pricingConfig }))
        ]);
        setPlans(plansRes.data);
        setAddons(addonsRes.data || []);
        setPricingConfig(configRes.data || pricingConfig);
      } catch (error) {
      }
    };
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const features = [
    {
      icon: ChefHat,
      title: "Complete POS System",
      description: "Fast, intuitive point of sale for restaurants, bars and hotels — table service, grouped checkout, split bills and quick sale modes."
    },
    {
      icon: Boxes,
      title: "Inventory & Procurement",
      description: "Track stock across stores, manage suppliers, purchase orders, goods received notes, requisitions, stock takes and live valuation."
    },
    {
      icon: ShieldCheck,
      title: "KRA eTIMS Compliant",
      description: "Real-time fiscal invoicing with KRA QR receipts, item classification sync, imports reconciliation and purchase submission.",
      highlight: true
    },
    {
      icon: CreditCard,
      title: "Card Terminals & Mobile Money",
      description: "Accept M-Pesa STK, Pesapal Cloud POS and Network International card terminals — on-device or cloud push-to-pay."
    },
    {
      icon: Smartphone,
      title: "Native Mobile Apps",
      description: "Android & iOS POS apps with PIN login, on-device receipt printing and fast, cache-first performance on the floor."
    },
    {
      icon: QrCode,
      title: "QR Code Menu & Ordering",
      description: "Give guests a branded scan-to-order menu — they browse, order and pay from their phone, straight into your POS."
    },
    {
      icon: Building2,
      title: "Multi-Property & Outlets",
      description: "Run multiple properties and outlets from one dashboard, with group-level reporting and per-outlet configuration."
    },
    {
      icon: Users,
      title: "Staff, Roles & Shifts",
      description: "Granular roles and permissions, PIN login, shift open/close with audit trail and end-of-day X/Z fiscal reports."
    },
    {
      icon: BarChart3,
      title: "Reports & Analytics",
      description: "Sales summaries, sales mix, payment reconciliation by channel and split-payment allocation — with Excel & PDF export."
    },
    {
      icon: Printer,
      title: "Receipts & Printing",
      description: "iMin built-in thermal, Bluetooth/ESC-POS and QZ Tray printing with fully customisable receipt templates and logo."
    }
  ];

  const benefits = [
    { icon: Cloud, text: "Cloud-based - Access anywhere" },
    { icon: Shield, text: "Secure & Reliable" },
    { icon: Clock, text: "30-Day Free Trial" },
    { icon: Star, text: "Local Support in Kenya" }
  ];

  const etimsPoints = [
    { icon: ReceiptText, title: "Fiscal invoicing + QR", text: "Every sale is signed and printed with a KRA-compliant QR code and fiscal details." },
    { icon: QrCode, title: "Automatic item sync", text: "Products are classified and synced to KRA with valid item classification codes." },
    { icon: FileCheck, title: "Sales, credit & debit notes", text: "Standard sales, credit notes, debit notes, proforma and training-mode invoices." },
    { icon: Boxes, title: "Imported items reconciliation", text: "Pull customs declarations, match to products, approve and stock-in — end to end." },
    { icon: Package, title: "Purchase (GRN) submission", text: "Goods received notes are submitted to eTIMS as purchases for full audit trails." },
    { icon: BarChart3, title: "X/Z fiscal reports & PLU", text: "Daily X and Z fiscal reports plus item-level PLU sales analysis." }
  ];

  const integrations = [
    { icon: Wallet, name: "M-Pesa (Daraja)", tag: "STK Push & C2B Paybill" },
    { icon: CreditCard, name: "Pesapal Cloud POS", tag: "Card terminal + M-Pesa STK" },
    { icon: CreditCard, name: "Network International", tag: "Push-to-Pay & on-device App-to-App" },
    { icon: Shield, name: "Cybersource", tag: "Secure card processing" },
    { icon: FileCheck, name: "KRA eTIMS", tag: "Fiscal invoicing & compliance" },
    { icon: Printer, name: "iMin Terminals", tag: "Built-in thermal receipt printing" },
    { icon: Printer, name: "QZ Tray", tag: "Windows / USB direct printing" },
    { icon: CreditCard, name: "Manual Card & M-Pesa", tag: "Reference capture fallback" }
  ];

  // Illustrative testimonials (generic personas — replace with real customer quotes when available).
  const testimonials = [
    { quote: "Every sale is fiscalised automatically — our KRA returns went from a weekend of work to a single click.", role: "Restaurant Owner", place: "Nairobi" },
    { quote: "We run three outlets on one dashboard and reconcile M-Pesa and card takings in minutes, not hours.", role: "Operations Manager", place: "Mombasa" },
    { quote: "The waiters love the mobile app — orders reach the kitchen instantly, even on a packed Friday night.", role: "Bar Manager", place: "Nakuru" },
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES', maximumFractionDigits: 0 }).format(amount);
  };

  // Helper to check if plan has unlimited/negotiable pricing
  const isUnlimited = (value) => !value || value === 0 || value >= 999;
  const isNegotiablePlan = (plan) => isUnlimited(plan.max_properties) && isUnlimited(plan.max_outlets) && isUnlimited(plan.max_users);

  // Format limit display
  const formatLimit = (value, singular, plural) => {
    if (isUnlimited(value)) return "Unlimited";
    return `${value} ${value > 1 ? plural : singular}`;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm z-50 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <ChefHat className="h-5 w-5 text-primary-foreground" />
              </div>
              <span className="text-xl font-bold text-gray-900">HostPoint</span>
            </div>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-6">
              <a href="#features" className="text-gray-600 hover:text-gray-900 text-sm font-medium">Features</a>
              <a href="#compliance" className="text-gray-600 hover:text-gray-900 text-sm font-medium">eTIMS</a>
              <a href="#integrations" className="text-gray-600 hover:text-gray-900 text-sm font-medium">Integrations</a>
              <a href="#pricing" className="text-gray-600 hover:text-gray-900 text-sm font-medium">Pricing</a>
              <a href="#contact" className="text-gray-600 hover:text-gray-900 text-sm font-medium">Contact</a>
              <Button variant="outline" onClick={() => navigate("/login")}>Sign In</Button>
              <Button onClick={() => navigate("/register")}>Get Started</Button>
            </div>

            {/* Mobile menu button */}
            <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t p-4 space-y-4">
            <a href="#features" className="block text-gray-600 hover:text-gray-900 font-medium" onClick={() => setMobileMenuOpen(false)}>Features</a>
            <a href="#compliance" className="block text-gray-600 hover:text-gray-900 font-medium" onClick={() => setMobileMenuOpen(false)}>eTIMS Compliance</a>
            <a href="#integrations" className="block text-gray-600 hover:text-gray-900 font-medium" onClick={() => setMobileMenuOpen(false)}>Integrations</a>
            <a href="#pricing" className="block text-gray-600 hover:text-gray-900 font-medium" onClick={() => setMobileMenuOpen(false)}>Pricing</a>
            <a href="#contact" className="block text-gray-600 hover:text-gray-900 font-medium" onClick={() => setMobileMenuOpen(false)}>Contact</a>
            <Button variant="outline" className="w-full" onClick={() => navigate("/login")}>Sign In</Button>
            <Button className="w-full" onClick={() => navigate("/register")}>Get Started</Button>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left max-w-2xl mx-auto lg:mx-0">
              <Badge variant="secondary" className="mb-4">
                🇰🇪 Built for Kenya&apos;s Hospitality Industry
              </Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 tracking-tight mb-6">
                The all-in-one, KRA eTIMS-ready POS for hospitality
              </h1>
              <p className="text-lg sm:text-xl text-gray-600 mb-8">
                HostPoint runs your restaurant, bar or hotel end to end — sales, stock, staff and
                payments — with real-time KRA eTIMS fiscal receipts and native mobile apps.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button size="lg" onClick={() => navigate("/register")} className="text-base">
                  Start Free Trial
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button size="lg" variant="outline" onClick={() => navigate("/login")} className="text-base">
                  Sign In to Dashboard
                </Button>
              </div>
              <p className="text-sm text-gray-500 mt-4">
                30-day free trial • No credit card required • Cancel anytime
              </p>
              {/* Trust chips */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-2 mt-8">
                {["KRA eTIMS", "M-Pesa", "Pesapal", "Network International", "Android & iOS apps"].map((t) => (
                  <span key={t} className="inline-flex items-center gap-1.5 rounded-full border bg-white px-3 py-1 text-xs font-medium text-gray-700">
                    <Check className="h-3.5 w-3.5 text-primary" /> {t}
                  </span>
                ))}
              </div>
            </div>

            {/* Animated fiscal receipt visual */}
            <div className="flex justify-center lg:justify-end">
              <HeroReceipt />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Bar */}
      <section className="py-8 bg-gray-50 border-y">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {benefits.map((benefit) => (
              <div key={benefit.text} className="flex items-center justify-center gap-2 text-gray-700">
                <benefit.icon className="h-5 w-5 text-primary" />
                <span className="text-sm font-medium">{benefit.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Everything You Need</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              From quick sales to full procurement, fiscal compliance and multi-outlet reporting —
              HostPoint has all the tools your hospitality business needs.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature) => (
              <Card
                key={feature.title}
                className={`border-0 shadow-sm hover:shadow-md transition-shadow ${feature.highlight ? 'ring-1 ring-primary/30 bg-primary/5' : ''}`}
              >
                <CardHeader>
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <feature.icon className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    {feature.title}
                    {feature.highlight && <Badge className="bg-primary text-[10px]">Compliance</Badge>}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Product Showcase Carousel */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50 border-t">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">See HostPoint in action</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              A clean, fast back office — from the daily dashboard to fully KRA-compliant fiscal reports.
            </p>
          </div>

          {/* Browser-frame mockup */}
          <div className="relative rounded-xl overflow-hidden shadow-2xl border border-gray-200 bg-white">
            <div className="flex items-center gap-2 px-4 py-3 border-b bg-gray-100">
              <span className="w-3 h-3 rounded-full bg-red-400" />
              <span className="w-3 h-3 rounded-full bg-yellow-400" />
              <span className="w-3 h-3 rounded-full bg-green-400" />
              <div className="ml-3 flex-1 max-w-md">
                <div className="h-5 rounded-full bg-white border text-[10px] text-gray-400 flex items-center px-3">
                  app.hostpoint.co.ke{showcase[activeShot].src.replace("/showcase", "").replace(".png", "")}
                </div>
              </div>
            </div>
            <div className="relative bg-gray-50" style={{ aspectRatio: "1440 / 900" }}>
              {showcase.map((s, i) => (
                <img
                  key={s.src}
                  src={s.src}
                  alt={s.title}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-700"
                  style={{ opacity: i === activeShot ? 1 : 0 }}
                />
              ))}
            </div>
          </div>

          {/* Caption + dots */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6">
            <div className="text-center sm:text-left">
              <p className="font-semibold text-gray-900">{showcase[activeShot].title}</p>
              <p className="text-sm text-gray-500">{showcase[activeShot].caption}</p>
            </div>
            <div className="flex items-center gap-2">
              {showcase.map((s, i) => (
                <button
                  key={s.src}
                  onClick={() => setActiveShot(i)}
                  aria-label={`Show ${s.title}`}
                  data-testid={`showcase-dot-${i}`}
                  className={`h-2.5 rounded-full transition-all ${i === activeShot ? "w-8 bg-primary" : "w-2.5 bg-gray-300 hover:bg-gray-400"}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* eTIMS Compliance Section */}
      <section id="compliance" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <Badge className="bg-primary mb-4">
                <ShieldCheck className="h-3.5 w-3.5 mr-1" /> KRA eTIMS Certified Integration
              </Badge>
              <h2 className="text-3xl font-bold mb-4">Stay compliant without the paperwork</h2>
              <p className="text-gray-300 mb-6 max-w-lg">
                HostPoint is built on KRA&apos;s eTIMS (OSCU) Apigee gateway. Every sale is fiscalised
                in real time and printed with a KRA QR code — while purchases, imports and daily
                reports flow to KRA automatically.
              </p>
              <ul className="space-y-3">
                {["23/23 KRA GavaConnect certification tests passing",
                  "Automatic retry & self-healing item classification",
                  "Works across every outlet and device you run"].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-gray-200">
                    <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-sm">{t}</span>
                  </li>
                ))}
              </ul>
              <Button className="mt-8" onClick={() => navigate("/register")}>
                Get eTIMS-ready today
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              {etimsPoints.map((p) => (
                <div key={p.title} className="rounded-xl bg-white/5 border border-white/10 p-5">
                  <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center mb-3">
                    <p.icon className="h-5 w-5 text-primary" />
                  </div>
                  <h4 className="font-semibold text-sm mb-1">{p.title}</h4>
                  <p className="text-xs text-gray-400">{p.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Scan-to-order QR Menu Section */}
      <section id="qr-menu" className="py-20 px-4 sm:px-6 lg:px-8 bg-primary/5 border-t overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: copy + demo link + QR */}
            <div>
              <Badge className="bg-primary mb-4">
                <QrCode className="h-3.5 w-3.5 mr-1" /> Scan-to-Order
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Let guests order from their table
              </h2>
              <p className="text-gray-600 mb-6 max-w-lg">
                Put a branded QR code on every table. Guests scan, browse your live menu,
                and place an order from their phone — no app to download. Orders drop straight
                into your POS and kitchen, fully fiscalised with KRA eTIMS.
              </p>
              <ul className="space-y-3 mb-8">
                {["No app download — works in any phone browser",
                  "Live menu, photos and prices, per outlet",
                  "Orders flow straight into your POS & kitchen",
                  "Pay by M-Pesa or card, or settle at the counter"].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-gray-700">
                    <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-sm">{t}</span>
                  </li>
                ))}
              </ul>

              <div className="flex flex-col sm:flex-row items-center gap-5">
                <Button size="lg" asChild data-testid="qr-demo-btn">
                  <a href="/menu/diani-plate" target="_blank" rel="noopener noreferrer">
                    Try the live demo menu
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </a>
                </Button>
                <div className="flex items-center gap-3">
                  <div className="bg-white p-2 rounded-lg border shadow-sm">
                    <QRCodeSVG value={demoMenuUrl} size={72} fgColor="#0f172a" level="M" />
                  </div>
                  <span className="text-xs text-gray-500 max-w-[9rem]">
                    Or scan to open the demo menu on your phone
                  </span>
                </div>
              </div>
            </div>

            {/* Right: phone mockup */}
            <div className="flex justify-center lg:justify-end">
              <div className="relative qr-phone-float">
                <div className="absolute -inset-8 bg-primary/20 blur-3xl rounded-full" aria-hidden="true" />
                <div className="relative w-[290px] rounded-[2.75rem] border-[11px] border-gray-900 bg-gray-900 shadow-2xl">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-gray-900 rounded-b-2xl z-10" aria-hidden="true" />
                  <div className="rounded-[1.9rem] overflow-hidden bg-white h-[560px]">
                    <img
                      src="/showcase/qr-menu-mobile.png"
                      alt="HostPoint scan-to-order QR menu on a phone"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <style>{`
          @keyframes qr-phone-float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-12px); } }
          .qr-phone-float { animation: qr-phone-float 6s ease-in-out infinite; }
          @media (prefers-reduced-motion: reduce) { .qr-phone-float { animation: none; } }
        `}</style>
      </section>

      {/* Payments & Integrations Section */}
      <section id="integrations" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Payments & Integrations</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Take every payment your customers want, and connect the terminals and printers you
              already use — all reconciled in one place.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {integrations.map((it) => (
              <Card key={it.name} className="border shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all">
                <CardContent className="pt-6 pb-5">
                  <div className="w-11 h-11 bg-primary/10 rounded-lg flex items-center justify-center mb-3">
                    <it.icon className="h-5 w-5 text-primary" />
                  </div>
                  <h4 className="font-semibold text-sm mb-1 text-gray-900">{it.name}</h4>
                  <p className="text-xs text-gray-500">{it.tag}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <p className="text-center text-sm text-gray-500 mt-8">
            Run multiple payment rails side by side with automatic reconciliation by channel — cash,
            M-Pesa and card, including split payments.
          </p>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-t">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Loved by hospitality teams</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Restaurants, bars and hotels across Kenya run smoother — and stay compliant — with HostPoint.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <Card key={t.role + t.place} className="border-0 shadow-sm">
                <CardContent className="pt-6">
                  <Quote className="h-7 w-7 text-primary/30 mb-3" />
                  <p className="text-gray-700 text-sm leading-relaxed mb-5">&ldquo;{t.quote}&rdquo;</p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm">
                      {t.role.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{t.role}</p>
                      <p className="text-xs text-gray-500">{t.place}, Kenya</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Simple, Transparent Pricing</h2>
            <p className="text-gray-600 max-w-2xl mx-auto mb-6">
              Choose the plan that fits your business. All plans include a 30-day free trial.
              Pay annually and get <span className="font-semibold text-primary">{pricingConfig.annual_discount_months} months FREE</span>!
            </p>

            {/* Billing Toggle */}
            <div className="flex items-center justify-center gap-4 mb-8">
              <Tabs value={billingCycle} onValueChange={setBillingCycle} className="w-auto">
                <TabsList className="grid grid-cols-2 w-64">
                  <TabsTrigger value="monthly">Monthly</TabsTrigger>
                  <TabsTrigger value="annually" className="relative">
                    Annually
                    <Badge className="absolute -top-2 -right-2 text-[10px] px-1.5 py-0.5 bg-green-500">Save {pricingConfig.annual_discount_months} mo</Badge>
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>

          {/* Responsive grid: 1 col mobile, 2 cols tablet, 3-4 cols desktop based on plan count */}
          <div className={`grid gap-6 max-w-7xl mx-auto px-4 ${
            plans.length <= 3
              ? 'md:grid-cols-2 lg:grid-cols-3'
              : plans.length === 4
                ? 'md:grid-cols-2 lg:grid-cols-4'
                : 'md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
          }`}>
            {plans.map((plan) => (
              <Card
                key={plan.id}
                className={`relative flex flex-col ${plan.code === 'pro' || plan.name.toLowerCase().includes('professional') ? 'border-primary shadow-lg ring-2 ring-primary' : isNegotiablePlan(plan) ? 'border-amber-400 bg-gradient-to-b from-amber-50 to-white' : 'border-gray-200'}`}
              >
                {(plan.code === 'pro' || plan.name.toLowerCase().includes('professional')) && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-primary">Most Popular</Badge>
                  </div>
                )}
                {isNegotiablePlan(plan) && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-amber-500">Enterprise</Badge>
                  </div>
                )}
                <CardHeader className="text-center pb-2">
                  <CardTitle className="text-xl">{plan.name}</CardTitle>
                  <CardDescription className="min-h-[40px] text-xs">{plan.description}</CardDescription>
                </CardHeader>
                <CardContent className="text-center">
                  <div className="mb-4">
                    {isNegotiablePlan(plan) ? (
                      <>
                        <span className="text-2xl font-bold text-amber-600">Negotiable</span>
                        <div className="text-xs text-gray-500 mt-1">Contact us for pricing</div>
                      </>
                    ) : (
                      <>
                        <span className="text-3xl font-bold text-gray-900">
                          {formatCurrency(billingCycle === "monthly" ? plan.price_monthly : Math.round(plan.price_yearly / 12))}
                        </span>
                        <span className="text-gray-500">/mo</span>
                        {billingCycle === "annually" && (
                          <div className="text-xs text-green-600 mt-1">
                            Billed {formatCurrency(plan.price_yearly)}/year
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  <ul className="space-y-2 text-left text-sm">
                    <li className="flex items-center gap-2">
                      <Home className="h-4 w-4 text-primary flex-shrink-0" />
                      <span>{formatLimit(plan.max_properties, 'Property', 'Properties')}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-primary flex-shrink-0" />
                      <span>{formatLimit(plan.max_outlets, 'Outlet', 'Outlets')}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-primary flex-shrink-0" />
                      <span>{formatLimit(plan.max_users, 'User', 'Users')}</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                      <span>Point of Sale</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                      <span>KRA eTIMS fiscal receipts</span>
                    </li>
                    {plan.includes_inventory && (
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                        <span>Inventory Management</span>
                      </li>
                    )}
                    {plan.includes_reports && (
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                        <span>Reports & Analytics</span>
                      </li>
                    )}
                    {plan.is_group_plan && (
                      <li className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                        <span>Group Dashboard</span>
                      </li>
                    )}
                  </ul>
                </CardContent>
                <CardFooter className="mt-auto">
                  <Button
                    className="w-full"
                    variant={(plan.code === 'pro' || plan.name.toLowerCase().includes('professional')) ? 'default' : isNegotiablePlan(plan) ? 'default' : 'outline'}
                    onClick={() => navigate(`/register?plan=${plan.code}`)}
                  >
                    {isNegotiablePlan(plan) ? 'Contact Sales' : 'Start Free Trial'}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>

          {/* Integration Add-ons */}
          {addons.length > 0 && (
            <div className="mt-16">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Integration Add-ons</h3>
                <p className="text-gray-600">
                  Powerful integrations billed per property per month
                </p>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 max-w-6xl mx-auto">
                {addons.map((addon) => (
                  <Card key={addon.id} className={`text-center ${addon.is_coming_soon ? 'opacity-60' : ''}`}>
                    <CardContent className="pt-6 pb-4">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-3">
                        <Zap className="h-5 w-5 text-primary" />
                      </div>
                      <h4 className="font-medium text-sm mb-1">{addon.name}</h4>
                      {addon.is_coming_soon ? (
                        <Badge variant="secondary" className="text-xs">Coming Soon</Badge>
                      ) : (
                        <p className="text-xs text-muted-foreground">
                          {formatCurrency(addon.price_per_property)}/property/mo
                        </p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          <p className="text-center text-sm text-gray-500 mt-8">
            All prices in Kenya Shillings (KES). Pay via M-Pesa or Card. Annual billing = {12 - pricingConfig.annual_discount_months} months paid upfront.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Transform Your Business?</h2>
          <p className="text-primary-foreground/80 mb-8 max-w-2xl mx-auto">
            Join hundreds of restaurants, bars, and hotels in Kenya already using HostPoint
            to streamline operations and stay KRA compliant.
          </p>
          <Button size="lg" variant="secondary" onClick={() => navigate("/register")}>
            Start Your 30-Day Free Trial
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Contact Sales Section */}
      <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50 border-t">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Talk to our team</h2>
            <p className="text-gray-600 mb-8">
              Setting up multiple outlets, migrating from another POS, or have a question about KRA
              eTIMS? Leave your details and we&apos;ll get back to you — usually within one business day.
            </p>
            <ul className="space-y-4 text-sm">
              <li className="flex items-center gap-3 text-gray-700">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Mail className="h-5 w-5 text-primary" />
                </div>
                <a href="mailto:support@hostpoint.co.ke" className="hover:text-primary">support@hostpoint.co.ke</a>
              </li>
              <li className="flex items-center gap-3 text-gray-700">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Phone className="h-5 w-5 text-primary" />
                </div>
                <a href="tel:+254700000000" className="hover:text-primary">+254 700 000 000</a>
              </li>
              <li className="flex items-center gap-3 text-gray-700">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-primary" />
                </div>
                <span>Nairobi, Kenya</span>
              </li>
            </ul>
          </div>

          <Card className="shadow-lg border-0">
            <CardContent className="pt-6">
              {contactDone ? (
                <div className="text-center py-10" data-testid="contact-success">
                  <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <Check className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Message sent</h3>
                  <p className="text-sm text-gray-600 mb-6">Thanks for reaching out — our team will be in touch shortly.</p>
                  <Button variant="outline" onClick={() => setContactDone(false)} data-testid="contact-send-another">
                    Send another message
                  </Button>
                </div>
              ) : (
                <form onSubmit={submitContact} className="space-y-4" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="c-name">Name *</Label>
                      <Input id="c-name" data-testid="contact-name" value={contact.name}
                        onChange={(e) => setContact({ ...contact, name: e.target.value })}
                        placeholder="Your name" className="mt-1" required />
                    </div>
                    <div>
                      <Label htmlFor="c-business">Business</Label>
                      <Input id="c-business" data-testid="contact-business" value={contact.business_name}
                        onChange={(e) => setContact({ ...contact, business_name: e.target.value })}
                        placeholder="Business name" className="mt-1" />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="c-email">Email *</Label>
                      <Input id="c-email" type="email" data-testid="contact-email" value={contact.email}
                        onChange={(e) => setContact({ ...contact, email: e.target.value })}
                        placeholder="you@business.co.ke" className="mt-1" required />
                    </div>
                    <div>
                      <Label htmlFor="c-phone">Phone</Label>
                      <Input id="c-phone" data-testid="contact-phone" value={contact.phone}
                        onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                        placeholder="07xx xxx xxx" className="mt-1" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="c-message">How can we help?</Label>
                    <Textarea id="c-message" data-testid="contact-message" value={contact.message}
                      onChange={(e) => setContact({ ...contact, message: e.target.value })}
                      placeholder="Tell us about your outlets and what you need…" className="mt-1" rows={4} />
                  </div>
                  <Button type="submit" className="w-full" disabled={contactSubmitting} data-testid="contact-submit">
                    {contactSubmitting ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</>
                    ) : (
                      <><Send className="mr-2 h-4 w-4" /> Send message</>
                    )}
                  </Button>
                  <p className="text-xs text-gray-400 text-center">We&apos;ll only use your details to respond to your enquiry.</p>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-900 text-gray-400">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-5 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                  <ChefHat className="h-5 w-5 text-primary-foreground" />
                </div>
                <span className="text-xl font-bold text-white">HostPoint</span>
              </div>
              <p className="text-sm">
                Complete, KRA eTIMS-ready POS and stock management for Kenya&apos;s hospitality industry.
              </p>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-white">Features</a></li>
                <li><a href="#compliance" className="hover:text-white">eTIMS Compliance</a></li>
                <li><a href="#integrations" className="hover:text-white">Integrations</a></li>
                <li><a href="#pricing" className="hover:text-white">Pricing</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/help" className="hover:text-white">Help Center</Link></li>
                <li><a href="#contact" className="hover:text-white">Contact Us</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link
                    to="/privacy-policy"
                    data-testid="footer-privacy-link"
                    className="hover:text-white"
                  >
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link
                    to="/terms-and-conditions"
                    data-testid="footer-terms-link"
                    className="hover:text-white"
                  >
                    Terms &amp; Conditions
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm">
                <li>Email: support@hostpoint.co.ke</li>
                <li>Phone: +254 700 000 000</li>
                <li>Nairobi, Kenya</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-center">
            <p>© {new Date().getFullYear()} HostPoint. All rights reserved.</p>
            <div className="flex items-center gap-6">
              <Link
                to="/privacy-policy"
                data-testid="footer-privacy-link-bottom"
                className="hover:text-white"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms-and-conditions"
                data-testid="footer-terms-link-bottom"
                className="hover:text-white"
              >
                Terms &amp; Conditions
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
